import type { z } from "zod";

import { ArrowLeft, Loader2, MailIcon } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import type { OTPSchema } from "@/components/form/custom-formfields";
import type { TEmailSchema } from "@/lib/schema";

import { CustomFormField, InputOTPForm } from "@/components/form/custom-formfields";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useResendEmail, useVerifyEmail } from "@/hooks/use-verify-email";
import { emailSchema } from "@/lib/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link, useNavigate, useRouter, useRouterState } from "@tanstack/react-router";

export const Route = createFileRoute("/_auth/verify-email")({
	component: VerifyEmail
});

function EmailInputForm({ onSubmit, isPending }: { isPending: boolean; onSubmit: (data: TEmailSchema) => void }) {
	const form = useForm<TEmailSchema>({
		resolver: zodResolver(emailSchema),
		defaultValues: {
			email: ""
		}
	});

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-6">
				<p>
					Enter the email address you used to sign up
				</p>
				<CustomFormField
					control={form.control}
					placeholder="Enter your email"
					className="h-14"
					type="email"
					Icon={MailIcon}
					name="email"
				/>
				<Button disabled={isPending} type="submit" className="h-14 w-full">
					{
						isPending

							? (
									<>
										<Loader2 size={20} className="animate-spin" />
										{" "}
										&nbsp;
										Loading....
									</>
								)
							: "Submit"
					}
				</Button>
			</form>
		</Form>
	);
}

interface VerifyOpts {
	email: string;
	hash?: string;
}
function VerifyEmail() {
	const router = useRouter();
	const navigate = useNavigate();
	const { state } = useRouterState({ select: state => state.location });
	const [verifyOpts, setVerifyOpts] = useState<VerifyOpts | null>(state.verifyEmailOpts ?? null);

	const verifyEmailMutation = useVerifyEmail();
	const onSubmit = (values: z.infer<typeof OTPSchema>) => {
		verifyEmailMutation.mutateAsync({
			data: {
				email: verifyOpts?.email || "",
				otp: values.pin,
				hash: verifyOpts?.hash || ""
			}
		});
	};

	const resendEmailMutation = useResendEmail();
	const handleResend = async (email: string) => {
		const { data } = await resendEmailMutation.mutateAsync({ email, endpoint: "send_verify_email" });
		setVerifyOpts(prev => ({ ...prev, hash: data, email: prev?.email || "" }));
	};

	return (
		<section>
			{verifyEmailMutation.status !== "success"
				? (

						<div className="flex flex-col justify-center space-y-10">
							<Button asChild variant="secondary" className="mr-auto">
								<Link to="/sign-up">
									<ArrowLeft />
								</Link>
							</Button>

							<h1 className="lg:text-36 mb-12 h-1 text-3xl font-semibold leading-9">
								Verify email
							</h1>
							{verifyOpts?.email || resendEmailMutation.isSuccess
								? (
										<>
											<p>
												Enter the 6 digit code sent to your email address
												{" "}
												<i className="text-primary">{verifyOpts?.email}</i>
											</p>
											{verifyEmailMutation.error && (
												<p className="text-center text-destructive">Something went wrong!</p>
											)}
											<InputOTPForm
												onSubmit={onSubmit}
												handleResend={() => handleResend(verifyOpts?.email ?? "")}
												isPending={verifyEmailMutation.isPending || resendEmailMutation.isPending}
											/>
											<Button
												variant="secondary"
												className="h-14"
												onClick={(() => router.history.back())}
											>
												Cancel
											</Button>
										</>
									)
								: !resendEmailMutation.isSuccess
									&& (
										<EmailInputForm
											onSubmit={(values) => {
												setVerifyOpts(prev => ({ ...prev, email: values.email }));
												handleResend(values.email);
											}}
											isPending={resendEmailMutation.isPending}
										/>
									)}
						</div>

					)
				: (
						<div className="flex h-[90vh] flex-col items-center justify-center space-y-6">
							<img src="/checkmark.png" className="size-28" alt="done" />
							<div>
								<h4 className="mb-0 text-center text-2xl font-semibold">
									Email Verified
								</h4>
								<p className="text-center text-2xl">
									Welcome to
									{" "}
									<span className="text-primary">Intasida</span>
								</p>
							</div>
							<Button
								onClick={() => navigate({ to: "/get-started" })}
								style={{ marginTop: "10rem" }}
								className="h-14 w-1/2"
							>
								Let's Get Started
							</Button>
						</div>
					)}
		</section>
	);
}
